import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { chain } from 'lodash';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { ComboCheckbox } from '../../../../../../../Checkbox/ComboCheckbox';
import VoxelButton from '../../../../../../../../voxel/Buttons/VoxelButton';
import VoxelButtonGroup from '../../../../../../../../voxel/Buttons/VoxelButtonGroup';
import VoxelInput from '../../../../../../../../voxel/Forms/VoxelInput';
import { useDebounceValue } from '../../../../../../../../../logic/otherUtilities/useDebounceValue';
import { FormCombination, FormWordGroup } from '../models/ComboFormState';
import { SetFormState } from '../models/SetFormState';

export interface ComboSelectBoxProps {
  wordGroups: FormWordGroup[];
  combinations: FormCombination[];
  setFormState: SetFormState;
  disabled: boolean;
}

const ComboSelectBox: React.FC<ComboSelectBoxProps> = ({
  setFormState,
  wordGroups,
  combinations,
  disabled,
}) => {
  const handleSelectWordCombination = useCallback((combinationId: string, checked: boolean) => {
    setFormState((prevState) => ({
      ...prevState,
      comboCombinations: prevState.comboCombinations.map((cc) => {
        if (cc.id === combinationId) {
          return { ...cc, selected: checked };
        }
        return cc;
      }),
    }));
  }, []);

  const handleSelectAll = useCallback(
    (combinationCount: number[], showCCWithoutSpace: boolean, comboSearchName: string) => {
      const textSearch = comboSearchName.toLowerCase();
      setFormState((prevState) => ({
        ...prevState,
        comboCombinations: prevState.comboCombinations.map((cc) => {
          if (
            combinationCount.some((c) => c === cc.wordGroups.length) &&
            (showCCWithoutSpace ? true : !(cc.removeSpacing > 0)) &&
            (textSearch.length ? cc.name.toLowerCase().includes(textSearch) : true)
          ) {
            return { ...cc, selected: true };
          }
          return cc;
        }),
      }));
    },
    []
  );

  const handleDeselectAll = useCallback(
    (combinationCount: number[], showCCWithoutSpace: boolean, comboSearchName: string) => {
      const textSearch = comboSearchName.toLowerCase();
      setFormState((prevState) => ({
        ...prevState,
        comboCombinations: prevState.comboCombinations.map((cc) => {
          if (
            combinationCount.some((c) => c === cc.wordGroups.length) &&
            (showCCWithoutSpace ? true : !(cc.removeSpacing > 0)) &&
            (textSearch.length ? cc.name.toLowerCase().includes(textSearch) : true)
          ) {
            return { ...cc, selected: false };
          }
          return cc;
        }),
      }));
    },
    []
  );
  const [showCCwgCount, setShowCCwgCount] = useState<number[]>(() => [1, 2]);
  const [showCCWithoutSpacing, setShowCCWithoutSpacing] = useState(false);

  const handleComboCountChange = useCallback((combo: number) => {
    setShowCCwgCount((prev) => {
      if (prev.includes(combo)) return prev.filter((c) => c != combo);

      return [...prev, combo];
    });
  }, []);

  // For calculation
  const debouncedValues = useDebounceValue({ wordGroups, combinations }, 500, [
    wordGroups,
    combinations,
  ]);

  // Tag search
  const [comboSearchName, setComboSearchName] = useState('');
  const [showCount, setShowCount] = useState(100);
  const handleLoadMore = useCallback(() => setShowCount((prev) => prev + 100), []);

  const filteredCombinations = useMemo(() => {
    const textSearch = comboSearchName.toLowerCase();
    return chain(debouncedValues.combinations)
      .orderBy([(cc) => cc.name.replace(/[,\s]+/gm, ''), (cc) => cc.removeSpacing], ['asc', 'asc'])
      .filter((cc) => showCCwgCount.some((c) => c === cc.wordGroups.length))
      .filter((cc) => (showCCWithoutSpacing ? true : !(cc.removeSpacing > 0)))
      .filter((cc) => (textSearch.length ? cc.name.toLowerCase().includes(textSearch) : true))
      .value();
  }, [comboSearchName, showCCWithoutSpacing, showCCwgCount, debouncedValues.combinations]);

  const finalCombinations = useMemo(
    () => filteredCombinations.slice(0, showCount),
    [filteredCombinations, showCount]
  );

  const combinationsToShow = useMemo(
    () =>
      [1, 2, 3].filter((combinationCount) =>
        debouncedValues.combinations.some((cc) => cc.wordGroups.length >= combinationCount)
      ),
    [debouncedValues.combinations.length]
  );

  return (
    <div className="flex flex-col gap-1 p-1 min-h-[200px]">
      {/* Search area */}
      <div className="flex flex-row gap-3 justify-between items-center">
        <div>
          <VoxelInput
            size={'sm'}
            onChange={(event) => setComboSearchName(event.target.value)}
            value={comboSearchName}
            startIcon={<FontAwesomeIcon icon={faSearch} />}
          />
        </div>
        <div>
          <VoxelButton
            title="AB"
            tooltip="Show combinations without spaces <word a><word b>"
            color="gray"
            active={showCCWithoutSpacing}
            onClick={() => setShowCCWithoutSpacing(!showCCWithoutSpacing)}
            size="md"
          />
        </div>
        <div>
          <VoxelButtonGroup gapClass="gap-0.5">
            {combinationsToShow.map((combinationCount) => (
              <VoxelButton
                size="md"
                key={combinationCount}
                color="gray"
                tooltip={`Show combinations with ${combinationCount} word${
                  combinationCount > 1 ? 's' : ''
                }`}
                active={showCCwgCount.some((g) => g === combinationCount)}
                title={combinationCount}
                onClick={() => handleComboCountChange(combinationCount)}
              />
            ))}
          </VoxelButtonGroup>
        </div>
      </div>
      <div className="flex flex-row flex-wrap gap-2.5 py-4">
        {finalCombinations.map((cc: FormCombination) => (
          <ComboCheckbox
            disabled={disabled}
            key={cc.id}
            cc={cc}
            handleSelectWordCombination={handleSelectWordCombination}
            wordGroups={debouncedValues.wordGroups}
          />
        ))}
        {showCount < filteredCombinations.length && (
          <VoxelButton onClick={handleLoadMore} title="Load more" />
        )}
      </div>
      <div className="flex-grow" />
      <div className="flex flex-row gap-3">
        <VoxelButton
          disabled={disabled}
          onClick={() => handleSelectAll(showCCwgCount, showCCWithoutSpacing, comboSearchName)}
          title={'Select all'}
          size="sm"
          fullWidth
          color="light-gray"
        />
        <VoxelButton
          disabled={disabled}
          onClick={() => handleDeselectAll(showCCwgCount, showCCWithoutSpacing, comboSearchName)}
          title={'Deselect all'}
          size="sm"
          fullWidth
          color="light-gray"
        />
      </div>
    </div>
  );
};

export default ComboSelectBox;
